import type { ProposalStatus } from '@buildbee/contracts';

/**
 * Proposals and templates: merge tags, an allowlist HTML sanitizer for rich text that ends up
 * on public pages and in emails, and proposal status transitions.
 */

// ---------------------------------------------------------------------------
// Merge tags
// ---------------------------------------------------------------------------

export const MERGE_TAGS = [
  { tag: 'contact.firstName', label: 'Contact first name' },
  { tag: 'contact.lastName', label: 'Contact last name' },
  { tag: 'contact.displayName', label: 'Contact full name' },
  { tag: 'contact.email', label: 'Contact email' },
  { tag: 'company.name', label: 'Company name' },
  { tag: 'deal.title', label: 'Deal title' },
  { tag: 'deal.value', label: 'Deal value' },
  { tag: 'project.name', label: 'Project name' },
  { tag: 'project.number', label: 'Project number' },
  { tag: 'estimate.number', label: 'Estimate number' },
  { tag: 'estimate.total', label: 'Estimate total' },
  { tag: 'proposal.link', label: 'Proposal signing link' },
  { tag: 'user.name', label: 'Your name' },
  { tag: 'user.email', label: 'Your email' },
  { tag: 'workspace.name', label: 'Your company name' },
] as const;

export type MergeContext = Record<string, Record<string, string | number | null | undefined> | undefined>;

export function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Replace `{{ path }}` and `{{ path | fallback }}` tags. Unknown tags render the fallback or an
 * empty string. Pass `{ html: true }` to HTML-escape inserted values.
 */
export function renderMergeTags(template: string, ctx: MergeContext, opts: { html?: boolean } = {}): string {
  return template.replace(/\{\{\s*([a-zA-Z][\w]*(?:\.[\w]+)*)\s*(?:\|\s*([^}]*?))?\s*\}\}/g, (_m, path: string, fallback?: string) => {
    const [scope, ...rest] = path.split('.');
    const key = rest.join('.');
    const raw = scope ? ctx[scope]?.[key] : undefined;
    const value = raw === null || raw === undefined || raw === '' ? (fallback ?? '') : String(raw);
    return opts.html ? escapeHtmlText(value) : value;
  });
}

/** Tags used in a template that the context cannot fill (for editor warnings). */
export function unresolvedMergeTags(template: string, ctx: MergeContext): string[] {
  const out: string[] = [];
  for (const m of template.matchAll(/\{\{\s*([a-zA-Z][\w]*(?:\.[\w]+)*)\s*(\|[^}]*)?\}\}/g)) {
    const path = m[1]!;
    const [scope, ...rest] = path.split('.');
    const v = scope ? ctx[scope]?.[rest.join('.')] : undefined;
    if ((v === undefined || v === null || v === '') && !m[2] && !out.includes(path)) out.push(path);
  }
  return out;
}

// ---------------------------------------------------------------------------
// HTML sanitizer (allowlist)
// ---------------------------------------------------------------------------

const ALLOWED_TAGS = new Set([
  'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'small', 'mark',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code',
  'ul', 'ol', 'li', 'a', 'span', 'div',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
]);
const VOID_TAGS = new Set(['br', 'hr', 'col']);
/** Elements dropped together with everything inside them. */
const DROP_WITH_CONTENT = new Set(['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template', 'svg', 'math', 'textarea', 'select', 'title', 'head']);
const ALLOWED_ATTRS: Record<string, readonly string[]> = {
  a: ['href', 'title'],
  td: ['colspan', 'rowspan', 'align'],
  th: ['colspan', 'rowspan', 'align', 'scope'],
  col: ['span'],
  ol: ['start'],
  p: ['align'],
};

function safeUrl(url: string): string | null {
  // eslint-disable-next-line no-control-regex -- strip control chars that browsers ignore in URLs
  const v = url.trim().replace(/[\u0000-\u001f\u007f\s]+/g, '');
  if (/^(https?:|mailto:|tel:)/i.test(v)) return url.trim();
  if (/^[#/]/.test(v) && !v.startsWith('//')) return url.trim();
  return null;
}

function decodeEntities(s: string): string {
  return s
    .replace(/&#x([0-9a-f]+);?/gi, (_m, h: string) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);?/g, (_m, d: string) => String.fromCodePoint(Number(d)))
    .replace(/&quot;/g, '"')
    .replace(/&apos;|&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

function sanitizeAttributes(tag: string, raw: string): string {
  const allowed = ALLOWED_ATTRS[tag];
  if (!allowed) return '';
  const out: string[] = [];
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
  for (const m of raw.matchAll(re)) {
    const name = m[1]!.toLowerCase();
    if (!allowed.includes(name)) continue;
    let value = decodeEntities(m[2] ?? m[3] ?? m[4] ?? '');
    if (name === 'href') {
      const safe = safeUrl(value);
      if (!safe) continue;
      value = safe;
    } else if (['colspan', 'rowspan', 'span', 'start'].includes(name)) {
      if (!/^\d{1,3}$/.test(value)) continue;
    } else if (name === 'align' || name === 'scope') {
      if (!/^[a-z]{1,10}$/i.test(value)) continue;
    }
    out.push(`${name}="${escapeHtmlText(value)}"`);
  }
  if (tag === 'a' && out.some((a) => a.startsWith('href='))) out.push('rel="noopener noreferrer nofollow"', 'target="_blank"');
  return out.length ? ` ${out.join(' ')}` : '';
}

/**
 * Allowlist HTML sanitizer for proposal bodies, templates and emails. Keeps basic formatting,
 * lists, tables and http(s)/mailto/tel links; drops scripts, styles, event handlers, unknown
 * tags (keeping their text) and comments. Unbalanced tags are closed at the end.
 */
export function sanitizeHtml(input: string): string {
  const html = input.replace(/<!--[\s\S]*?(-->|$)/g, '').replace(/<!\[CDATA\[[\s\S]*?(\]\]>|$)/g, '').replace(/<![^>]*>/g, '').replace(/<\?[\s\S]*?(\?>|$)/g, '');
  const out: string[] = [];
  const open: string[] = [];
  const tagRe = /<\/?([a-zA-Z][a-zA-Z0-9-]*)((?:\s+[^\s"'>/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*)\s*\/?>/g;
  let last = 0;
  let dropUntil: string | null = null;
  const pushText = (text: string) => {
    if (dropUntil || !text) return;
    out.push(text.replace(/</g, '&lt;').replace(/>/g, '&gt;'));
  };
  for (const m of html.matchAll(tagRe)) {
    const idx = m.index;
    pushText(html.slice(last, idx));
    last = idx + m[0].length;
    const tag = m[1]!.toLowerCase();
    const closing = m[0].startsWith('</');
    if (dropUntil) {
      if (closing && tag === dropUntil) dropUntil = null;
      continue;
    }
    if (DROP_WITH_CONTENT.has(tag)) {
      if (!closing && !m[0].endsWith('/>')) dropUntil = tag;
      continue;
    }
    if (!ALLOWED_TAGS.has(tag)) continue;
    if (closing) {
      const pos = open.lastIndexOf(tag);
      if (pos === -1) continue;
      while (open.length > pos) out.push(`</${open.pop()!}>`);
      continue;
    }
    out.push(`<${tag}${sanitizeAttributes(tag, m[2] ?? '')}>`);
    if (!VOID_TAGS.has(tag)) open.push(tag);
  }
  pushText(html.slice(last));
  while (open.length) out.push(`</${open.pop()!}>`);
  return out.join('');
}

/** Plain text -> paragraphs/line breaks (escaped). */
export function textToHtml(text: string): string {
  return text
    .split(/\r?\n\s*\r?\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtmlText(p).replace(/\r?\n/g, '<br>')}</p>`)
    .join('');
}

/** Heuristic: treat bodies containing tags as HTML, everything else as plain text. */
export function bodyToHtml(body: string): string {
  return /<\/?[a-z][\s\S]*>/i.test(body) ? sanitizeHtml(body) : textToHtml(body);
}

// ---------------------------------------------------------------------------
// Proposal status
// ---------------------------------------------------------------------------

export type ProposalEvent = 'send' | 'view' | 'sign' | 'decline' | 'void' | 'expire' | 'reopen';

const NEXT: Record<ProposalEvent, Partial<Record<ProposalStatus, ProposalStatus>>> = {
  send: { draft: 'sent', sent: 'sent', viewed: 'sent', expired: 'sent', declined: 'sent' },
  view: { sent: 'viewed', viewed: 'viewed' },
  sign: { sent: 'signed', viewed: 'signed' },
  decline: { sent: 'declined', viewed: 'declined' },
  void: { draft: 'void', sent: 'void', viewed: 'void', declined: 'void', expired: 'void' },
  expire: { sent: 'expired', viewed: 'expired' },
  reopen: { void: 'draft', declined: 'draft', expired: 'draft' },
};

/** Next status for an event, or null when the event is not allowed in the current status. */
export function proposalTransition(current: ProposalStatus, event: ProposalEvent): ProposalStatus | null {
  return NEXT[event][current] ?? null;
}

export function isProposalExpired(p: { status: ProposalStatus; expiresAt: Date | null }, now: Date): boolean {
  return (p.status === 'sent' || p.status === 'viewed') && !!p.expiresAt && p.expiresAt.getTime() < now.getTime();
}

/** Only drafts (and re-opened proposals) may be edited; sent proposals are frozen for signing. */
export function isProposalEditable(status: ProposalStatus): boolean {
  return status === 'draft';
}
